import { createTheme } from "@mui/material";

// Light theme colors
const lightTheme = {
  palette: {
    mode: 'light',
    primary: {
      light: "#1976D2",
      main: "#3f50b5",
      dark: "#002884",
      contrastText: "#fff",
    },
    secondary: {
      main: "#f50057",
      contrastText: "#fff",
    },
    background: {
      default: "#F6F6F6",
      paper: "#FFFFFF",
      table: "#FFFFFF",
      tableHeader: "#D8DCDF",
      signalsHeader: "#E4E4E4",
      signalsRow: "#F8F8F8",
    },
    text: {
      primary: "#000000",
      secondary: "#666666",
      disabled: "#999999",
    },
    grey: {
      50: "#FAFAFA",
      100: "#F5F5F5 ",
      200: "#EAEAEA",
      300: "#D3D3D3",
      400: "#BDBDBD",
      500: "#9E9E9E",
      600: "#757575",
      700: "#616161",
      800: "#424242",
      900: "#212121 ",
    },
    lightGrey: {
      textColor: "#424242",
      backgroundBody: "#F6F6F6",
      tableHeader: "#D8DCDF",
      signalsHeader: "#E4E4E4",
      signalsRow: "#F8F8F8",
    },
    red: {
      shortPositions: "#C3444429",
      100: "#F86868",
      200: "#D32F2F",
      300: "#880000",
    },
    blue: {
      100: "#DCE6F0",
      200: "#424E5F",
      300: "#0B0B45",
    },
    green: {
      longPositions: "#3DB4652B",
      Aquamarine: "#7FFFD4",
      ArmyGreen: "#454B1B",
      EmeraldGreen: "#50C878",
      FernGreen: "#4F7942",
      ForestGreen: "#228B22",
      GrassGreen: "#7CFC00",
      Green: "#008000",
      HunterGreen: "#355E3B",
      Jade: "#00A36C",
      JungleGreen: "#2AAA8A",
      KellyGreen: "#4CBB17",
      LightGreen: "#90EE90",
      LimeGreen: "#32CD32",
      SeaGreen: "#2E8B57",
      Malachite: "#0BDA51",
      MintGreen: "#98FB98",
      ShamrockGreen: "#009E60",
    },
    divider: "#E0E0E0",
    success: {
      main: "#4CAF50",
      light: "#81C784",
      dark: "#388E3C",
    },
    error: {
      main: "#F44336",
      light: "#E57373",
      dark: "#D32F2F",
    },
    warning: {
      main: "#FF9800",
      light: "#FFB74D",
      dark: "#F57C00",
    },
    info: {
      main: "#2196F3",
      light: "#64B5F6",
      dark: "#1976D2",
    },
  },
  typography: {
    fontFamily: `'Inter', 'SF Pro Display', 'Roboto', 'Arial', sans-serif`,
    body11: {
      fontSize: "7px",
    },
    bodyText12: {
      fontSize: "12px",
      fontWeight: 500,
    },
    bodyText13: {
      fontSize: "13px",
      fontWeight: 500,
    },
    bodyText14: {
      fontSize: "14px",
    },
    bodyText15: {
      fontSize: "15px",
      fontWeight: 400,
    },
    bodyText16: {
      fontSize: "16px",
      fontWeight: 600,
    },
    bodyText18: {
      fontSize: "18px",
      fontWeight: 600,
    },
    bodyText20: {
      fontSize: "20px",
      fontWeight: 500,
    },
    h5: {
      fontSize: "0.8rem",
      fontWeight: 500,
    },
    h6: {
      fontSize: "2px",
    },
  },
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #E0E0E0',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#D8DCDF',
            borderBottom: '2px solid #BDBDBD',
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: '1px solid #E0E0E0',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#424242',
          color: '#FFFFFF',
          fontSize: '12px',
          padding: '8px 12px',
          borderRadius: '4px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          '& .MuiTooltip-arrow': {
            color: '#424242',
          },
        },
        arrow: {
          color: '#424242',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: '#FFFFFF',
          border: 'none',
          '&:hover': {
            background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)',
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
          },
        },
        outlined: {
          border: '2px solid #1976d2',
          color: '#1976d2',
          background: 'transparent',
          '&:hover': {
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            color: '#FFFFFF',
            border: '2px solid transparent',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)',
          },
        },
        text: {
          color: '#1976d2',
          '&:hover': {
            background: 'rgba(25, 118, 210, 0.1)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
  },
};

// Dark theme colors
const darkTheme = {
  palette: {
    mode: 'dark',
    primary: {
      light: "#64B5F6",
      main: "#90CAF9",
      dark: "#1976D2",
      contrastText: "#000",
    },
    secondary: {
      main: "#F48FB1",
      contrastText: "#000",
    },
    background: {
      default: "#121212",
      paper: "#1E1E1E",
      table: "#1E1E1E",
      tableHeader: "#2D2D2D",
      signalsHeader: "#2D2D2D",
      signalsRow: "#262626",
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#B0B0B0",
      disabled: "#666666",
    },
    grey: {
      50: "#212121",
      100: "#2D2D2D",
      200: "#424242",
      300: "#616161",
      400: "#757575",
      500: "#9E9E9E",
      600: "#BDBDBD",
      700: "#D3D3D3",
      800: "#E0E0E0",
      900: "#1f1f1f",
    },
    lightGrey: {
      textColor: "#B0B0B0",
      backgroundBody: "#121212",
      tableHeader: "#2D2D2D",
      signalsHeader: "#2D2D2D",
      signalsRow: "#262626",
    },
    red: {
      shortPositions: "#FF525229",
      100: "#FF8A80",
      200: "#FF5252",
      300: "#D32F2F",
    },
    blue: {
      100: "#82B1FF",
      200: "#64B5F6",
      300: "#1976D2",
    },
    green: {
      longPositions: "#4CAF5029",
      Aquamarine: "#7FFFD4",
      ArmyGreen: "#8BC34A",
      EmeraldGreen: "#4CAF50",
      FernGreen: "#8BC34A",
      ForestGreen: "#4CAF50",
      GrassGreen: "#8BC34A",
      Green: "#4CAF50",
      HunterGreen: "#388E3C",
      Jade: "#4CAF50",
      JungleGreen: "#4CAF50",
      KellyGreen: "#8BC34A",
      LightGreen: "#8BC34A",
      LimeGreen: "#8BC34A",
      SeaGreen: "#4CAF50",
      Malachite: "#4CAF50",
      MintGreen: "#8BC34A",
      ShamrockGreen: "#4CAF50",
    },
    divider: "#424242",
    success: {
      main: "#4CAF50",
      light: "#81C784",
      dark: "#388E3C",
    },
    error: {
      main: "#F44336",
      light: "#E57373",
      dark: "#D32F2F",
    },
    warning: {
      main: "#FF9800",
      light: "#FFB74D",
      dark: "#F57C00",
    },
    info: {
      main: "#2196F3",
      light: "#64B5F6",
      dark: "#1976D2",
    },
  },
  typography: {
    fontFamily: `'Inter', 'SF Pro Display', 'Roboto', 'Arial', sans-serif`,
    body11: {
      fontSize: "7px",
    },
    bodyText12: {
      fontSize: "12px",
      fontWeight: 500,
    },
    bodyText13: {
      fontSize: "13px",
      fontWeight: 500,
    },
    bodyText14: {
      fontSize: "14px",
    },
    bodyText15: {
      fontSize: "15px",
      fontWeight: 400,
    },
    bodyText16: {
      fontSize: "16px",
      fontWeight: 600,
    },
    bodyText18: {
      fontSize: "18px",
      fontWeight: 600,
    },
    bodyText20: {
      fontSize: "20px",
      fontWeight: 500,
    },
    h5: {
      fontSize: "0.8rem",
      fontWeight: 500,
    },
    h6: {
      fontSize: "2px",
    },
  },
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #424242',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#2D2D2D',
            borderBottom: '2px solid #616161',
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: '1px solid #424242',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#424242',
          color: '#FFFFFF',
          fontSize: '12px',
          padding: '8px 12px',
          borderRadius: '4px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          '& .MuiTooltip-arrow': {
            color: '#424242',
          },
        },
        arrow: {
          color: '#424242',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(21, 101, 192, 0.4)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
          color: '#FFFFFF',
          border: 'none',
          '&:hover': {
            background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(21, 101, 192, 0.4)',
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: '0 4px 15px rgba(21, 101, 192, 0.4)',
          },
        },
        outlined: {
          border: '2px solid #64B5F6',
          color: '#64B5F6',
          background: 'transparent',
          '&:hover': {
            background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
            color: '#FFFFFF',
            border: '2px solid transparent',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(21, 101, 192, 0.4)',
          },
        },
        text: {
          color: '#64B5F6',
          '&:hover': {
            background: 'rgba(100, 181, 246, 0.1)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
  },
};

// Create theme instances
const lightThemeInstance = createTheme(lightTheme);
const darkThemeInstance = createTheme(darkTheme);

// Default theme (light)
const theme = lightThemeInstance;

export { lightThemeInstance, darkThemeInstance };
export default theme;
